import PropTypes from 'prop-types';
import { BsGlobe, BsTrash, BsStarFill, BsChevronDown, BsChevronUp } from 'react-icons/bs';

import HeaderFilter from '#globalComponents/Table/filters/HeaderFilter';
import YesNoFilterControl from '#globalComponents/Table/filters/YesNoFilterControl';
import { CELL_LINK, CELL_TEXT_MUTED, ROW_ACTION_BUTTON } from '#globalComponents/Table/tableStyles';
import TruncatedText from '#globalComponents/TruncatedText/TruncatedText';

import { formatEurCompact } from '../../../utils/formatters';
import CompanyKindBadge from '../../CompanyKind/CompanyKindBadge';
import AIScoreBadge from '../AIScoreBadge/AIScoreBadge';

import {
	ContactsFilterControl,
	DateRangeFilterControl,
	MinMaxFilterControl,
	MinSelectFilterControl,
	MinSliderFilterControl,
} from './LeadFilterControls';
import LeadProfileCell from './LeadProfileCell';
import { NameFilterControl, ProfileFilterControl } from './ProfileFilterControls';

export const NAME_COLUMN_WIDTH = 250;

const hasValue = (value) => value !== null && value !== undefined;

const LeadNameCell = ({ lead, vocabulary, onNameClick }) => (
	<div className="flex min-w-0 items-center gap-1.5">
		<button
			onClick={() => onNameClick(lead.id)}
			className="min-w-0 flex-1 text-left hover:text-primary-600 transition-colors"
		>
			<TruncatedText text={lead.companyName} className="font-semibold text-text" />
		</button>
		<CompanyKindBadge company={lead} vocabulary={vocabulary} size="xs" className="flex min-w-0 max-w-[120px]" />
	</div>
);

LeadNameCell.propTypes = {
	lead: PropTypes.object.isRequired,
	vocabulary: PropTypes.object,
	onNameClick: PropTypes.func.isRequired,
};

/**
 * Builds the column config for the /Leads companies table.
 *
 * @param {Object} config
 * @param {Object} config.filters - Active table filters, driving the funnel indicators.
 * @param {Function} config.onFilterChange - Receives (paramKey, value) from the filter controls.
 * @param {Set<string>} config.expandedLeads - Lead ids whose contact row is open.
 * @param {Function} config.onToggleExpand - Receives a lead id.
 * @param {Function} config.onNameClick - Receives a lead id.
 * @param {Function} config.onDelete - Receives the lead object.
 * @param {Object} [config.vocabulary] - Targeting vocabulary: value labels and filter options.
 * @returns {Array<Object>} Table column definitions.
 */
export const buildLeadColumns = ({
	filters,
	onFilterChange,
	expandedLeads,
	onToggleExpand,
	onNameClick,
	onDelete,
	vocabulary,
}) => [
	{
		key: 'name',
		label: 'Unternehmensname',
		sortable: true,
		stickyWidth: NAME_COLUMN_WIDTH,
		minWidth: NAME_COLUMN_WIDTH,
		grow: true,
		headerAccessory: vocabulary ? (
			<NameFilterControl filters={filters} onChange={onFilterChange} vocabulary={vocabulary} />
		) : undefined,
		render: (lead) => <LeadNameCell lead={lead} vocabulary={vocabulary} onNameClick={onNameClick} />,
	},
	{
		key: 'profile',
		label: 'Profil',
		minWidth: 220,
		grow: true,
		headerAccessory: vocabulary ? (
			<ProfileFilterControl filters={filters} onChange={onFilterChange} vocabulary={vocabulary} />
		) : undefined,
		render: (lead) => <LeadProfileCell lead={lead} vocabulary={vocabulary} />,
	},
	{
		key: 'contacts',
		label: 'Kontakte',
		sortable: true,
		align: 'center',
		minWidth: 110,
		headerAccessory: (
			<ContactsFilterControl
				hasContacts={filters.hasContacts}
				contactChannel={filters.contactChannel}
				onChange={onFilterChange}
			/>
		),
		render: (lead) => {
			const totalCount = Number(lead.contactCount) || 0;
			if (totalCount === 0) return <span className="text-bgBase-700">0</span>;

			const isExpanded = expandedLeads.has(lead.id);
			return (
				<button
					onClick={(e) => {
						e.stopPropagation();
						onToggleExpand(lead.id);
					}}
					title={isExpanded ? 'Kontakte ausblenden' : 'Kontakte anzeigen'}
					className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium text-primary-500 transition-colors ${
						isExpanded
							? 'bg-primary-200'
							: 'bg-primary-100 group-hover/row:bg-primary-200 group-data-[state]/row:bg-primary-200'
					}`}
				>
					<span>{totalCount}</span>
					{isExpanded ? <BsChevronUp className="w-3 h-3" /> : <BsChevronDown className="w-3 h-3" />}
				</button>
			);
		},
	},
	{
		key: 'revenue',
		label: 'Umsatz',
		sortable: true,
		align: 'right',
		minWidth: 130,
		headerAccessory: (
			<HeaderFilter
				label="Umsatz filtern"
				active={filters.revenueMin !== undefined || filters.revenueMax !== undefined}
			>
				<MinMaxFilterControl
					heading="Umsatz"
					unit="Mio. €"
					unitFactor={1000000}
					minParamKey="revenueMin"
					maxParamKey="revenueMax"
					minValue={filters.revenueMin}
					maxValue={filters.revenueMax}
					onChange={onFilterChange}
				/>
			</HeaderFilter>
		),
		// Latest financial KPIs (revenue/employeeCount/... with their *IsEstimate and *Year
		// siblings) live flat on the lead object, served by the companies endpoint.
		render: (lead) => {
			const isEstimate = lead.revenueIsEstimate && hasValue(lead.revenue);
			return (
				<>
					<span className="font-medium" title={isEstimate ? 'Geschätzter Wert' : undefined}>
						{hasValue(lead.revenue)
							? `${lead.revenueIsEstimate ? '~' : ''}${formatEurCompact(lead.revenue)}`
							: '–'}
					</span>
					{hasValue(lead.revenue) && lead.revenueYear && (
						<div className={CELL_TEXT_MUTED}>{lead.revenueYear}</div>
					)}
				</>
			);
		},
	},
	{
		key: 'employees',
		label: 'Mitarbeiter',
		sortable: true,
		align: 'right',
		minWidth: 110,
		headerAccessory: (
			<HeaderFilter
				label="Mitarbeiter filtern"
				active={filters.employeesMin !== undefined || filters.employeesMax !== undefined}
			>
				<MinMaxFilterControl
					heading="Mitarbeiter"
					minParamKey="employeesMin"
					maxParamKey="employeesMax"
					minValue={filters.employeesMin}
					maxValue={filters.employeesMax}
					onChange={onFilterChange}
				/>
			</HeaderFilter>
		),
		render: (lead) => {
			const isEstimate = lead.employeeCountIsEstimate && hasValue(lead.employeeCount);
			return (
				<>
					<span className="font-medium" title={isEstimate ? 'Geschätzter Wert' : undefined}>
						{hasValue(lead.employeeCount)
							? `${lead.employeeCountIsEstimate ? '~' : ''}${lead.employeeCount}`
							: '–'}
					</span>
					{hasValue(lead.employeeCount) && lead.employeeCountYear && (
						<div className={CELL_TEXT_MUTED}>{lead.employeeCountYear}</div>
					)}
				</>
			);
		},
	},
	{
		key: 'ai-score',
		label: 'AI-Score',
		sortable: true,
		align: 'center',
		minWidth: 180,
		headerAccessory: (
			<HeaderFilter label="AI-Score filtern" active={filters.aiScoreMin !== undefined}>
				<MinSliderFilterControl
					heading="AI-Score mindestens"
					paramKey="aiScoreMin"
					value={filters.aiScoreMin}
					onChange={onFilterChange}
				/>
			</HeaderFilter>
		),
		render: (lead) => (
			<AIScoreBadge score={lead.aiScore} criteria={lead.aiCriteria} companyName={lead.companyName} />
		),
	},
	{
		key: 'rating',
		label: 'Bewertung',
		sortable: true,
		align: 'center',
		minWidth: 140,
		headerAccessory: (
			<HeaderFilter label="Bewertung filtern" active={filters.ratingMin !== undefined}>
				<MinSelectFilterControl
					heading="Bewertung"
					paramKey="ratingMin"
					value={filters.ratingMin}
					onChange={onFilterChange}
				/>
			</HeaderFilter>
		),
		render: (lead) =>
			lead.rating ? (
				<div>
					<div className="flex items-center justify-center gap-1">
						<span className="font-semibold text-accent">{lead.rating.toFixed(1)}</span>
						<BsStarFill className="w-3.5 h-3.5 text-accent" />
					</div>
					{lead.reviewCount > 0 && <div className={CELL_TEXT_MUTED}>{lead.reviewCount} Bewertungen</div>}
				</div>
			) : (
				<span>–</span>
			),
	},
	{
		key: 'website',
		label: 'Website',
		minWidth: 200,
		grow: true,
		headerAccessory: (
			<YesNoFilterControl
				heading="Website vorhanden"
				paramKey="hasWebsite"
				value={filters.hasWebsite}
				onChange={onFilterChange}
			/>
		),
		render: (lead) =>
			lead.websiteUrl ? (
				<a
					href={lead.websiteUrl}
					target="_blank"
					rel="noreferrer"
					className={`flex items-center gap-2 truncate ${CELL_LINK}`}
					onClick={(e) => e.stopPropagation()}
				>
					<BsGlobe className="w-4 h-4 flex-shrink-0" />
					<span className="truncate">{lead.websiteUrl.replace(/^https?:\/\/(www\.)?/, '')}</span>
				</a>
			) : (
				<span>–</span>
			),
	},
	{
		key: 'location',
		label: 'Standort',
		minWidth: 200,
		render: (lead) => {
			const address = [lead.addressStreet, lead.addressPostal, lead.addressCity].filter(Boolean).join(', ');
			return address ? (
				<div>
					<div className="truncate">{address}</div>
					{!lead.addressStreet && (
						<div className={`italic ${CELL_TEXT_MUTED}`}>Genaue Adresse nicht gefunden</div>
					)}
				</div>
			) : (
				<span>–</span>
			);
		},
	},
	{
		key: 'created-at',
		label: 'Erstellt am',
		sortable: true,
		minWidth: 130,
		headerAccessory: (
			<HeaderFilter
				label="Erstelldatum filtern"
				active={filters.createdFrom !== undefined || filters.createdTo !== undefined}
			>
				<DateRangeFilterControl
					heading="Erstellt am"
					fromParamKey="createdFrom"
					toParamKey="createdTo"
					fromValue={filters.createdFrom}
					toValue={filters.createdTo}
					onChange={onFilterChange}
				/>
			</HeaderFilter>
		),
		render: (lead) => (
			<span>
				{lead.createdAt
					? `${new Date(lead.createdAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}, ${new Date(lead.createdAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`
					: '–'}
			</span>
		),
	},
	{
		key: 'actions',
		label: 'Aktionen',
		align: 'right',
		sticky: 'right',
		cellClassName: 'whitespace-nowrap',
		headerClassName: 'whitespace-nowrap',
		render: (lead) => (
			<div className="flex justify-end gap-1">
				<button
					onClick={(e) => {
						e.stopPropagation();
						onDelete(lead);
					}}
					className={ROW_ACTION_BUTTON}
					title="Löschen"
				>
					<BsTrash />
				</button>
			</div>
		),
	},
];
